import React, { useState, useEffect } from "react";
import { Info, ChevronDown } from "lucide-react";

interface Props {
  originalPrice: string;
  discountedPrice: string;
  firstOrderDiscountPercent?: number;
  recurringDiscountPercent?: number;
  onTypeChange?: (type: "subscribe" | "one-time") => void;
  onPeriodChange?: (billingCycle: number, interval: number) => void;
}

const SubscribeAndSave: React.FC<Props> = ({
  discountedPrice,
  firstOrderDiscountPercent = 0,
  recurringDiscountPercent = 0,
  onTypeChange,
  originalPrice,
  onPeriodChange,
}) => {
  const [purchaseType, setPurchaseType] = useState<"subscribe" | "one-time">(
    "subscribe"
  );

  // "monthly" | "weekly" | "custom"
  const [frequencyTab, setFrequencyTab] = useState<
    "monthly" | "weekly" | "custom"
  >("monthly");
  const [customMonths, setCustomMonths] = useState<number>(2); // Default to 2 Months if custom clicked
  const [showDropdown, setShowDropdown] = useState<boolean>(false);

  useEffect(() => {
    onTypeChange?.(purchaseType);

    if (purchaseType === "subscribe") {
      let billingCycle = 2; // Monthly = 2
      let interval = 1;

      if (frequencyTab === "weekly") {
        billingCycle = 1; // Weekly = 1
        interval = 1;
      } else if (frequencyTab === "custom") {
        billingCycle = 3; // Custom = 3
        interval = customMonths;
      }

      onPeriodChange?.(billingCycle, interval);
    }
  }, [purchaseType, frequencyTab, customMonths]);

  if (firstOrderDiscountPercent <= 0 && recurringDiscountPercent <= 0) {
    return null;
  }

  const numericPrice = parseFloat(originalPrice.replace(/[^\d.]/g, "")) || 0;
  const currencySymbol = discountedPrice.replace(/[\d.\s]/g, "") || "AED";

  const initialOrderPrice = (
    numericPrice *
    (1 - firstOrderDiscountPercent / 100)
  ).toFixed(2);
  const futureOrdersPrice = (
    numericPrice *
    (1 - recurringDiscountPercent / 100)
  ).toFixed(2);

  const customOptions = [2, 3, 4, 6];
  const handleTypeSelect = (type: "subscribe" | "one-time") => {
    setPurchaseType(type);
  };

  return (
<div className="flex flex-col gap-2 w-full max-w-[360px] select-none text-left">
  
  {/* 1. AUTOSHIP (SUBSCRIBE) COMPACT BOX */}
  <div
    onClick={() => handleTypeSelect("subscribe")}
    className={`border rounded-xl p-3 cursor-pointer transition-all relative ${
      purchaseType === "subscribe"
        ? "border-2 border-emerald-500 bg-white shadow-2xs ring-1 ring-emerald-500/10"
        : "border-slate-200 bg-white hover:border-slate-300"
    }`}
  >
    {/* Floating Discount Badge sitting ON the top line */}
    {firstOrderDiscountPercent > 0 && (
      <p className="absolute -top-3.5 right-4 bg-white px-1.5 text-rose-600 text-base font-extrabold whitespace-nowrap z-10">
        Save {firstOrderDiscountPercent}% 
      </p>
    )}

    {/* Radio Indicator Button */}
    <div className="absolute top-3 left-3 flex items-center justify-center">
      <div
        className={`w-4 h-4 rounded-full border flex items-center justify-center ${
          purchaseType === "subscribe"
            ? "border-emerald-600"
            : "border-slate-300"
        }`}
      >
        {purchaseType === "subscribe" && (
          <div className="w-2 h-2 rounded-full bg-emerald-600" />
        )}
      </div>
    </div>

    <div className="pl-6 text-xs">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 text-slate-900 font-extrabold">
          <span>🔄 Autoship</span>

          {/* Tooltip Wrapper */}
          <div className="relative inline-block group">
            <Info
              size={13}
              className="text-slate-400 cursor-help hover:text-slate-600 transition-colors"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Tooltip Content Container */}
            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 hidden group-hover:block w-56 p-3 bg-white text-slate-700 text-[10px] font-normal rounded-xl border border-slate-200/80 shadow-xl z-50 pointer-events-none text-left leading-relaxed animate-in fade-in zoom-in-95 duration-100">
              <p className="font-extrabold border-b border-slate-100 pb-1 mb-1.5 text-emerald-600 text-[11px]">
                Autoship Perks:
              </p>
              <ul className="space-y-1.5 list-disc list-inside text-slate-600 font-medium">
                <li>Convenient & Hassle-Free – Get your products delivered automatically, right when you need them.</li>
                <li>Never Run Out – Stay stocked without remembering to reorder.</li>
                <li>Flexible & Easy – Manage your Auto-Ship schedule anytime.</li>
                <li>Cancel Anytime – Cancel after your first Auto-Ship order is completed, with no long-term commitment.</li>
              </ul>
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-white z-10" />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-slate-200/80" />
            </div>
          </div>
        </div>
      </div>
      
      <p className="text-slate-400 text-[10px] font-medium mt-0.5">
        Easy, automated repeat deliveries
      </p>

      {/* Quick Tab Options Toggle */}
      {purchaseType === "subscribe" && (
        <div className="mt-2 flex items-center gap-1 relative">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setFrequencyTab("monthly");
              setShowDropdown(false);
            }}
            className={`px-1.5 py-1 text-[9px] rounded font-bold border transition ${
              frequencyTab === "monthly"
                ? "bg-emerald-50 border-emerald-600 text-emerald-800"
                : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100"
            }`}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setFrequencyTab("weekly");
              setShowDropdown(false);
            }}
            className={`px-1.5 py-1 text-[9px] rounded font-bold border transition ${
              frequencyTab === "weekly"
                ? "bg-emerald-50 border-emerald-600 text-emerald-800"
                : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100"
            }`}
          >
            Weekly
          </button>

          {/* Dynamic Dropdown Selector */}
          <div className="relative">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setFrequencyTab("custom");
                setShowDropdown(!showDropdown);
              }}
              className={`px-1.5 py-1 text-[9px] rounded font-bold border transition flex items-center gap-0.5 ${
                frequencyTab === "custom"
                  ? "bg-emerald-50 border-emerald-600 text-emerald-800"
                  : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100"
              }`}
            >
              <span>
                {frequencyTab === "custom"
                  ? `${customMonths} Mos`
                  : "Custom"}
              </span>
              <ChevronDown
                size={8}
                className={`transition-transform duration-200 ${
                  showDropdown ? "rotate-180" : ""
                }`}
              />
            </button>

            {showDropdown && frequencyTab === "custom" && (
              <div className="absolute right-0 top-full mt-1 z-30 bg-white border border-slate-200 rounded-lg shadow-lg py-0.5 w-28 text-left max-h-40 overflow-y-auto animate-in fade-in slide-in-from-top-1 duration-150">
                {customOptions.map((months) => (
                  <button
                    key={months}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCustomMonths(months);
                      setShowDropdown(false);
                    }}
                    className={`w-full px-2 py-1.5 text-[10px] font-bold text-left transition hover:bg-slate-50 ${
                      customMonths === months
                        ? "text-emerald-700 bg-emerald-50/40"
                        : "text-slate-600"
                    }`}
                  >
                    {months} {months === 1 ? "Month" : "Months"}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pricing Details */}
      <div className="mt-3 flex flex-col gap-0.5">
        <div className="flex items-baseline gap-1">
          <span className="text-xl font-black text-rose-600 font-mono">
            {currencySymbol} {initialOrderPrice}
          </span>
          <span className="text-slate-400 text-[10px] font-bold uppercase">
            this order
          </span>
        </div>
      </div>

      <div className="mt-2 pt-2 border-t border-slate-100 flex items-center gap-1.5 text-slate-700 font-bold text-[11px]">
        <span className="font-mono">
          {currencySymbol} {futureOrdersPrice}
        </span>
        <span className="text-slate-400 font-medium text-[10px]">
          ({recurringDiscountPercent}% off) future orders
        </span>
      </div>
    </div>
  </div>

  {/* 2. ONE-TIME PURCHASE BOX */}
  <div
    onClick={() => handleTypeSelect("one-time")}
    className={`border rounded-xl p-3 cursor-pointer transition-all relative ${
      purchaseType === "one-time"
        ? "border-emerald-600 bg-white shadow-2xs ring-1 ring-emerald-600/10"
        : "border-slate-200 bg-white hover:border-slate-300"
    }`}
  >
    <div className="absolute top-4 left-3 flex items-center justify-center">
      <div
        className={`w-4 h-4 rounded-full border flex items-center justify-center ${
          purchaseType === "one-time"
            ? "border-emerald-600"
            : "border-slate-300"
        }`}
      >
        {purchaseType === "one-time" && (
          <div className="w-2 h-2 rounded-full bg-emerald-600" />
        )}
      </div>
    </div>
    <div className="pl-6 text-xs flex items-center justify-between">
      <h3 className="text-slate-500 font-bold">Buy once</h3>
      <p className="text-sm font-black text-slate-800 font-mono">
        {discountedPrice}
      </p>
    </div>
  </div>
</div>
  );
};

export default SubscribeAndSave;
